import { ReactElement, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourseContent,
  selectCourseContentByID,
  selectCourseContentStatusByID,
} from 'src/v3/classroom-v2/classroom-v2.slice'
import {
  fetchCourseProgress,
  selectCourseProgressStatusByID,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import { CourseOverview } from 'src/v3/classroom-v2/components/course-overview'
import { CourseOverviewSkeleton } from 'src/v3/classroom-v2/components/overview-skeleton'

export default (): ReactElement => {
  let { courseID } = useParams()
  let dispatch = useAppDispatch()

  let content = useAppSelector(selectCourseContentByID(courseID ?? ''))
  let status = useAppSelector(selectCourseContentStatusByID(courseID ?? ''))
  let progressStatus = useAppSelector(
    selectCourseProgressStatusByID(courseID ?? ''),
  )

  useEffect(() => {
    if (courseID && status === 'uninitialized') {
      dispatch(fetchCourseContent(courseID))
    }
  }, [courseID, status])

  // The overview needs progress too, because whether this learner has finished
  // the course is part of what the page says about it. Deliberately NOT paired
  // with resetWizard the way the player's mount is: this route is a place to read
  // from, not a visit that starts anything, and wiping the wizard here would
  // throw away a quiz attempt or survey draft for a learner who only stepped out
  // to look at the syllabus.
  //
  // Gated on 'uninitialized' so it doesn't re-ask on every return trip. That also
  // keeps it clear of a restart, which clears this status: the middleware's own
  // refetch is dispatched synchronously from restartCourse.fulfilled, so its
  // pending action has already moved the status off 'uninitialized' by the time
  // this effect could run and ask a second time.
  useEffect(() => {
    if (courseID && progressStatus === 'uninitialized') {
      dispatch(fetchCourseProgress(courseID))
    }
  }, [courseID, progressStatus])

  if (status === 'rejected') {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h1">Course unavailable</Typography>
        <Typography color="text.secondary">
          We couldn&apos;t load this course. Please try again later.
        </Typography>
      </Box>
    )
  }

  if (!content) {
    return <CourseOverviewSkeleton />
  }

  return <CourseOverview content={content} />
}
